import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { clearLastSelectedCarSlug } from '../lib/lastCarSelection';
import { apiClient } from '../lib/apiClient';
import * as Sentry from "@sentry/react";

let cachedMetadata: any = null;

export async function getClientMetadata() {
  if (cachedMetadata) return cachedMetadata;

  const meta: Record<string, any> = {
    browser: navigator.userAgent,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
  };
  
  try {
    // Try to get IP and Location from a free service
    const res = await fetch('https://ipapi.co/json/');
    if (res.ok) {
      const geo = await res.json();
      meta.ip = geo.ip;
      meta.location = `${geo.city}, ${geo.country_name}`;
      meta.country_code = geo.country_code;
    }
  } catch {
    // Fallback if IP service is down
  }
  
  cachedMetadata = meta;
  return meta;
}

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  adminLoading: boolean;
  signInWithMagicLink: (email: string, language: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);

  const checkAdmin = async (userId: string | undefined) => {
    if (!userId) {
      setIsAdmin(false);
      setAdminLoading(false);
      return;
    }
    setAdminLoading(true);
    try {
      const { data } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();
      setIsAdmin(!!data);
    } catch {
      setIsAdmin(false);
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSession(session);
        setUser(session.user);
        checkAdmin(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user?.id) {
          checkAdmin(session.user.id);
          Sentry.setUser({ 
            id: session.user.id, 
            email: session.user.email 
          });
        } else {
          Sentry.setUser(null);
        }

        // Log successful sign-in - only once per actual login event
        if (_event === 'SIGNED_IN' && session?.user?.email) {
          const lastSignIn = session.user.last_sign_in_at;
          const localStorageKey = `auth_logged_${session.user.id}`;
          const alreadyLogged = localStorage.getItem(localStorageKey);

          if (lastSignIn && lastSignIn !== alreadyLogged) {
            localStorage.setItem(localStorageKey, lastSignIn);

            const provider = session.user.app_metadata.provider || 
                            session.user.app_metadata.providers?.[0] || 
                            'magic_link';
            
            // Logging manual SIGN_IN event to DB...
            void (async () => { 
              try { 
                const metadata = await getClientMetadata();
                await supabase.rpc('log_login_attempt', { 
                  p_email: session.user.email, 
                  p_provider: `success_${provider}`, 
                  p_metadata: { ...metadata, provider: `success_${provider}` }
                });

                // Track 'registered' if the user was created very recently (e.g., last 30 seconds)
                const createdAt = new Date(session.user.created_at).getTime();
                const now = Date.now();
                if (now - createdAt < 30000) {
                  // This is likely a registration
                  await supabase.rpc('track_user_event', { 
                    p_event_type: 'registered'
                  });
                }
              } catch (e) {
                console.warn('Auth logging skipped:', e);
              } 
            })();
          }
        }

        // Clean up hash fragment after successful auth
        if (session && window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signInWithMagicLink = async (email: string, language: string) => {
    try {
      await apiClient('https://mycarcosts.gtreulands.workers.dev/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language, redirectTo: window.location.origin }),
        context: 'signInWithMagicLink',
      });
      // Log the login attempt for admin tracking (fire-and-forget)
      void (async () => { 
        try { 
          // Handled explicitly in LoginForm now
        } catch {} 
      })();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    clearLastSelectedCarSlug(); // Clear last selected car on logout
    setIsAdmin(false);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAdmin,
        adminLoading,
        signInWithMagicLink,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
